import type { JobTag } from '@seek/chalice-types';

import type { GetCareerFeedQuery } from 'src/graphql/graphql';
import { logger } from 'src/modules/logger';

import type { CompanyEVPProps } from '../CompanyEVP/CompanyEVP';
import type { Job } from '../SharedComponent/JobCard/JobCard';

// Graph Types
type CareerFeed = NonNullable<NonNullable<GetCareerFeedQuery['careerFeed']>>;
type GraphFeedEdge = NonNullable<CareerFeed['edges']>[number];
type GraphFeedItem = NonNullable<GraphFeedEdge>['node'];
type GraphFeedJob = Extract<GraphFeedItem, { __typename: 'CareerFeedJob' }>;
type GraphFeedCompanyEvp = Extract<
  GraphFeedItem,
  { __typename: 'CareerFeedCompanyEvp' }
>;

const coerceNullToUndefined = <T>(value: T | null): T | undefined =>
  value ?? undefined;

const NODES_BEFORE_SURVEY_LIMIT = 20;

// Local Types
type CareerFeedJob = Job & {
  type: 'JOB';
  position: number;
};
type CareerFeedCompanyEvp = CompanyEVPProps & {
  type: 'COMPANY_EVP';
};
type CareerFeedRecsInterceptSurvey = {
  type: 'RECS_INTERCEPT_SURVEY';
};
export type CareerFeedItem =
  | CareerFeedJob
  | CareerFeedRecsInterceptSurvey
  | CareerFeedCompanyEvp;

// Local predicates
export const isCareerFeedJob = (item: CareerFeedItem): item is CareerFeedJob =>
  item.type === 'JOB';
export const isCareerFeedRecsInterceptSurvey = (
  item: CareerFeedItem,
): item is CareerFeedRecsInterceptSurvey =>
  item.type === 'RECS_INTERCEPT_SURVEY';
export const isCareerFeedCompanyEvp = (
  item: CareerFeedItem,
): item is CareerFeedCompanyEvp => item.type === 'COMPANY_EVP';

// Mappers from GraphQL to local types
export const mapJobBadgesToJobTags = (
  jobBadges: GraphFeedJob['jobBadges'],
): JobTag[] =>
  (jobBadges?.badgeDetails || [])
    .map((badge) => ({
      label: badge?.displayText,
      type: badge?.badge,
    }))
    .filter(
      (badge): badge is JobTag => Boolean(badge.label) && Boolean(badge.type),
    );
const mapToCareerFeedJob = (
  feedJob: GraphFeedJob,
  index: number,
): CareerFeedJob | undefined => {
  if (!feedJob.job) {
    return;
  }

  const {
    job: { advertiser, id, listedAt, location, products, salary, title },
    solMetadata,
    savedJob,
    jobBadges,
  } = feedJob;

  return {
    type: 'JOB',
    advertiserName: advertiser.name,
    id,
    isSaved: Boolean(savedJob),
    listingDateLabel: listedAt?.label,
    locationLabel: location.label,
    salaryCurrencyLabel: coerceNullToUndefined(salary?.currencyLabel),
    salaryLabel: salary?.label,
    solMetadata,
    srcLogo: products?.branding?.logo?.url,
    tags: mapJobBadgesToJobTags(jobBadges),
    title,
    bullets: coerceNullToUndefined(products?.bullets),
    position: index + 1,
  };
};
export const mapToCareerFeedCompanyEvp = (
  feedCompanyEvp: GraphFeedCompanyEvp,
): CareerFeedCompanyEvp | undefined => {
  const {
    companyId,
    companyProfile,
    companyReviewsAISummary,
    solMetadata,
    serviceToken,
  } = feedCompanyEvp;
  const { data } = companyProfile || {};
  const { branding, canonicalUrl, name, overview } = data || {};
  const { industry, size } = overview || {};
  const { cover, logo } = branding || {};
  const { aISummaryContent } = companyReviewsAISummary || {};
  const { positiveAndChallengesSummary } = aISummaryContent || {};

  if (
    !name ||
    !canonicalUrl ||
    !logo ||
    !positiveAndChallengesSummary ||
    // Ensure that at least size or industry is provided
    !(size || industry)
  ) {
    logger.warn(
      { input: { companyProfile, companyReviewsAISummary } },
      '[CareerFeedCompanyEvp]: Ignoring company EVP due to missing required fields.',
    );
    return;
  }

  return {
    type: 'COMPANY_EVP',
    id: companyId,
    name,
    cover: coerceNullToUndefined(cover),
    industry: coerceNullToUndefined(overview?.industry),
    logo,
    size: coerceNullToUndefined(overview?.size),
    solMetadata,
    summary: positiveAndChallengesSummary,
    url: `${canonicalUrl}?serviceToken=${serviceToken}`,
    serviceToken,
  };
};

const mapEdgeToCareerFeedItem = (
  feedItem: GraphFeedEdge,
  index: number,
): CareerFeedItem | undefined => {
  switch (feedItem?.node?.__typename) {
    case 'CareerFeedJob':
      return mapToCareerFeedJob(feedItem.node, index);
    case 'CareerFeedCompanyEvp': {
      return mapToCareerFeedCompanyEvp(feedItem.node);
    }
    default:
      return undefined;
  }
};

type MapCareerFeedItemOptions = {
  limitBeforeSurvey: number;
};

export const mapQueryToCareerFeedItem = (
  careerFeedQueryResponse: GetCareerFeedQuery | undefined,
  { limitBeforeSurvey }: MapCareerFeedItemOptions = {
    limitBeforeSurvey: NODES_BEFORE_SURVEY_LIMIT,
  },
): CareerFeedItem[] | undefined => {
  const feedItems = careerFeedQueryResponse?.careerFeed?.edges
    ?.map<CareerFeedItem | undefined>(mapEdgeToCareerFeedItem)
    .filter((item): item is CareerFeedItem => Boolean(item));

  // Temporarily adding the survey in the mapping layer.
  // Ideally, this should come from the backend.
  if (feedItems && feedItems.length >= limitBeforeSurvey) {
    feedItems.splice(limitBeforeSurvey, 0, {
      type: 'RECS_INTERCEPT_SURVEY',
    });
  }

  return feedItems;
};
